import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import addDays from 'date-fns/addDays';
import startOfToday from 'date-fns/startOfToday';
import isEmpty from 'lodash/isEmpty';
import qs from 'qs';

import { BusSearchFormProps, RouterQueryObject, WindowLocationObject } from '../BusSearchForm';
import { FIELDS, SEARCH_KEYS } from '../constants';
import fetchGeoPointById from '../helpers/fetchGeoPointById';
import fetchStationById from '../helpers/fetchStationById';
import { getSearchHistory } from '../helpers/saveSearchHistory';
import trackError from '../helpers/trackError';
import {
  checkIfAdultsAreValid,
  checkIfChildrenAgesAreValid,
  checkIfDateIsValid,
  checkIfGeoIdIsValid,
} from '../helpers/validators';
import { GeoAutocompleteOption, GeoPoint, SearchForm, Station, TargetType } from '../types';

type SourceValues = {
  childrenAges: string[];
  adults: string;
  date: string | null;
  departureGeoPointId?: string;
  destinationGeoPointId?: string;
  departureStationId?: string;
  arrivalStationId?: string;
};

const getPreparedChildrenAges = (childrenAgesValues: string[]) => {
  return childrenAgesValues.map((age) => Number(age));
};

const createOptionFromGeoPoint = (geoPoint: GeoPoint): GeoAutocompleteOption => {
  return {
    country: geoPoint.country,
    district: geoPoint.district,
    label: geoPoint.name,
    region: geoPoint.region,
    targetType: TargetType.GeoPoint,
    translit: geoPoint.translit,
    value: geoPoint.id,
    geoPoint: {
      country: geoPoint.country,
      district: geoPoint.district,
      id: geoPoint.id,
      name: geoPoint.name,
      region: geoPoint.region,
      translit: geoPoint.translit,
    },
  };
};

const createOptionFromStation = (station: Station): GeoAutocompleteOption => {
  return {
    country: station.countryName,
    district: station.districtName,
    label: station.name,
    region: station.regionName,
    targetType: TargetType.Station,
    translit: station.translit,
    value: station.id,
    geoPoint: {
      country: station.countryName,
      district: station.districtName,
      id: station.geoPointId,
      name: station.geoPointName,
      region: station.regionName,
      translit: station.geoPointTranslit,
    },
  };
};

const isRouterObject = (
  location: RouterQueryObject | WindowLocationObject
): location is RouterQueryObject => Boolean((location as RouterQueryObject).query);

const queryParamsAdapter = (queryParams: Record<string, string>): Record<string, any> => {
  if (queryParams.date === 'null') {
    return { ...queryParams, date: null };
  }

  return queryParams;
};

const useInitialValues = ({
  location,
  preventAutofillFromHistory,
}: {
  location?: BusSearchFormProps['location'];
  preventAutofillFromHistory?: boolean;
}) => {
  const [areInitialValuesLoading, setAreInitialValuesLoading] = useState(false);
  const [shouldChooseDate, setShouldChooseDate] = useState(false);
  const { setValue } = useFormContext<SearchForm>();
  const isDepartureAborted = useRef<boolean>(false);
  const isDestinationAborted = useRef<boolean>(false);

  const setInitialData = useCallback(async () => {
    const queryParams = (() => {
      if (!location) return {};

      if (isRouterObject(location)) {
        return queryParamsAdapter(location.query);
      }

      return queryParamsAdapter(
        qs.parse(location.search, { ignoreQueryPrefix: true }) as Record<string, string>
      );
    })();

    const generateInitialValues = async (
      sourceValues: SourceValues,
      type: 'fromUrl' | 'fromLocalStorage'
    ) => {
      if (checkIfChildrenAgesAreValid(sourceValues.childrenAges)) {
        setValue(FIELDS.CHILDREN_AGES, getPreparedChildrenAges(sourceValues.childrenAges));
      }

      if (checkIfAdultsAreValid(sourceValues.adults)) {
        setValue(FIELDS.ADULTS, Number(sourceValues.adults));
      }

      if (checkIfDateIsValid(sourceValues.date)) {
        if (sourceValues.date === null) {
          setShouldChooseDate(true);
        } else {
          const urlDate = new Date(sourceValues.date);
          const currentDate = startOfToday();
          setValue(FIELDS.DATE, urlDate < currentDate ? addDays(currentDate, 1) : urlDate);
        }
      }

      const hasInvalidGeoId =
        !checkIfGeoIdIsValid(sourceValues.departureGeoPointId) ||
        !checkIfGeoIdIsValid(sourceValues.destinationGeoPointId) ||
        !checkIfGeoIdIsValid(sourceValues.departureStationId) ||
        !checkIfGeoIdIsValid(sourceValues.arrivalStationId);

      if (hasInvalidGeoId && type === 'fromUrl') {
        return true;
      }

      setAreInitialValuesLoading(true);

      const shouldFetchDepartureStation = Boolean(sourceValues.departureStationId);
      const shouldFetchArrivalStation = Boolean(sourceValues.arrivalStationId);
      const shouldFetchDepartureGeoPoint =
        Boolean(sourceValues.departureGeoPointId) && !shouldFetchDepartureStation;
      const shouldFetchDestinationGeoPoint =
        Boolean(sourceValues.destinationGeoPointId) && !shouldFetchArrivalStation;

      const [departureStation, arrivalStation, departureGeoPoint, destinationGeoPoint] =
        await Promise.all([
          shouldFetchDepartureStation
            ? fetchStationById(String(sourceValues.departureStationId))
            : null,
          shouldFetchArrivalStation
            ? fetchStationById(String(sourceValues.arrivalStationId))
            : null,
          shouldFetchDepartureGeoPoint
            ? fetchGeoPointById(String(sourceValues.departureGeoPointId))
            : null,
          shouldFetchDestinationGeoPoint
            ? fetchGeoPointById(String(sourceValues.destinationGeoPointId))
            : null,
        ]);

      if (departureStation && !isDepartureAborted.current) {
        setValue(FIELDS.DEPARTURE, departureStation.name);
        setValue(FIELDS.DEPARTURE_OPTION, createOptionFromStation(departureStation));
      }

      if (arrivalStation && !isDestinationAborted.current) {
        setValue(FIELDS.DESTINATION, arrivalStation.name);
        setValue(FIELDS.DESTINATION_OPTION, createOptionFromStation(arrivalStation));
      }

      if (departureGeoPoint && !isDepartureAborted.current) {
        setValue(FIELDS.DEPARTURE, departureGeoPoint.name);
        setValue(FIELDS.DEPARTURE_OPTION, createOptionFromGeoPoint(departureGeoPoint));
      }

      if (destinationGeoPoint && !isDestinationAborted.current) {
        setValue(FIELDS.DESTINATION, destinationGeoPoint.name);
        setValue(FIELDS.DESTINATION_OPTION, createOptionFromGeoPoint(destinationGeoPoint));
      }

      return false;
    };

    const setValuesFromUrlOrShouldSetFromLocalStorage = async () => {
      if (isEmpty(queryParams)) {
        return true;
      }

      const {
        [SEARCH_KEYS.CHILDREN_AGES]: childrenAgesQueryValues,
        [SEARCH_KEYS.ADULTS]: adultsQueryValue,
        [SEARCH_KEYS.DATE]: dateQueryValue,
        [SEARCH_KEYS.START_GEO_ID]: departureGeoPointIdQueryValue,
        [SEARCH_KEYS.END_GEO_ID]: destinationGeoPointIdQueryValue,
        [SEARCH_KEYS.DEPARTURE_STATION_ID]: departureStationIdQueryValue,
        [SEARCH_KEYS.ARRIVAL_STATION_ID]: arrivalStationIdQueryValue,
      } = queryParams;

      // eslint-disable-next-line no-return-await
      return await generateInitialValues(
        {
          adults: adultsQueryValue,
          childrenAges: childrenAgesQueryValues,
          date: dateQueryValue,
          departureGeoPointId: departureGeoPointIdQueryValue,
          destinationGeoPointId: destinationGeoPointIdQueryValue,
          departureStationId: departureStationIdQueryValue,
          arrivalStationId: arrivalStationIdQueryValue,
        },
        'fromUrl'
      );
    };

    const shouldSetFromLocalStorage = await setValuesFromUrlOrShouldSetFromLocalStorage();

    if (shouldSetFromLocalStorage && !preventAutofillFromHistory) {
      type HistoryDateType = string;
      const firstHistoryItem: SearchForm<HistoryDateType> = getSearchHistory()[0];

      if (firstHistoryItem) {
        const sourceValues: SourceValues = {
          adults: String(firstHistoryItem.adults),
          childrenAges: firstHistoryItem.childrenAges?.map((age) => String(age)),
          date: firstHistoryItem.date,
        };

        const shouldPassDepartureStationId =
          firstHistoryItem.departureOption.targetType === TargetType.Station;

        const shouldPassArrivalStationId =
          firstHistoryItem.destinationOption.targetType === TargetType.Station;

        if (shouldPassDepartureStationId) {
          sourceValues.departureStationId = String(firstHistoryItem.departureOption.value);
        } else {
          sourceValues.departureGeoPointId = String(firstHistoryItem.departureOption.value);
        }

        if (shouldPassArrivalStationId) {
          sourceValues.arrivalStationId = String(firstHistoryItem.destinationOption.value);
        } else {
          sourceValues.destinationGeoPointId = String(firstHistoryItem.destinationOption.value);
        }

        await generateInitialValues(sourceValues, 'fromLocalStorage');
      }
    }
  }, []);

  useEffect(() => {
    setInitialData()
      .catch((error) => trackError(error))
      .finally(() => setAreInitialValuesLoading(false));
  }, []);

  return {
    areInitialValuesLoading,
    shouldChooseDate,
    abortInitialDepartureSettingIfNeeded: () => {
      if (!isDepartureAborted.current) {
        isDepartureAborted.current = true;
      }
    },
    abortInitialDestinationSettingIfNeeded: () => {
      if (!isDestinationAborted.current) {
        isDestinationAborted.current = true;
      }
    },
  };
};

export default useInitialValues;
